import React, { useEffect, useState, useRef } from "react";
import { PrivacyLevel } from "util/userUtils";
import { ViewerRelation } from "util/constants";
import DetailPanel from "ui/DetailPanel";
import { Blend } from "lucide-react";
import { Progress } from "ui/ProgressBar";

interface RadarPoint {
  category: string;
  currentUserValue: number | null;
  targetUserValue: number | null;
  label: string;
  alignment: number;
}

interface AlignmentData {
  response: number;
  importance: number;
  privacy: PrivacyLevel;
}

interface UserAlignmentData {
  [statementId: number]: {
    response?: number;
    importance?: number;
  };
}

interface Statement {
  id: number;
  statement: string;
  category: string;
  reverse: boolean;
}

interface SimilarStatement {
  id: number;
  statement: string;
  currentUserImportance: number;
  targetUserImportance: number;
  agreement: number;
}

interface AlignmentSummaryTileProps {
  statements: Statement[];
  getTargetUserAlignmentData: (statementId: number) => AlignmentData | null;
  getCurrentUserAlignmentData: (statementId: number) => { response?: number; importance?: number } | null;
  onStatementSelect: (statementId: number) => void;
  targetUsername: string;
  currentUserViewerRelation: ViewerRelation;
}

// Define all categories with their labels
const CATEGORIES = [
  { key: "content_discontent", label1: "Content", label2: "Discontent" },
  { key: "nationalism_cosmopolitanism", label1: "Nationalism", label2: "Cosmopolitanism" },
  { key: "theocracy_secularism", label1: "Theocracy", label2: "Secularism" },
  { key: "authority_liberty", label1: "Authority", label2: "Liberty" },
  { key: "capitalism_socialism", label1: "Capitalism", label2: "Socialism" },
  { key: "dogma_science", label1: "Dogma", label2: "Science" },
  { key: "autocracy_democracy", label1: "Autocracy", label2: "Democracy" },
  { key: "conservatism_progressivism", label1: "Conservatism", label2: "Progressivism" }
];

// Define a set of category keys for quick lookups
const CATEGORY_KEYS = new Set(CATEGORIES.map(cat => cat.key));

// Updated colors
const TARGET_USER_COLOR = "rgba(99, 102, 241, 0.9)"; // indigo-500
const TARGET_USER_FILL = "rgba(99, 102, 241, 0.5)"; // indigo-500 with transparency
const CURRENT_USER_COLOR = "rgba(0, 82, 134, 0.9)"; // eoe-blue
const CURRENT_USER_FILL = "rgba(0, 82, 134, 0.7)"; // eoe-blue with transparency
const DEFAULT_TEXT_COLOR = "#666";
const HIGH_MATCH_COLOR = "#16a34a"; // green-600 (changed from #22c55e)

// Weights for similaritea calculation
const ALIGNMENT_WEIGHT = 0.6;
const IMPORTANCE_WEIGHT = 0.4;

const AlignmentSummaryTile: React.FC<AlignmentSummaryTileProps> = ({
  statements,
  getTargetUserAlignmentData,
  getCurrentUserAlignmentData,
  onStatementSelect,
  targetUsername,
  currentUserViewerRelation
}) => {
  const [similariteaPercentage, setSimilariteaPercentage] = useState<number>(0);
  const [pureAlignmentPercentage, setPureAlignmentPercentage] = useState<number>(0);
  const [radarData, setRadarData] = useState<RadarPoint[]>([]);
  const [similarTopics, setSimilarTopics] = useState<number>(0);
  const [differentTopics, setDifferentTopics] = useState<number>(0);
  const [mutuallyImportantTopics, setMutuallyImportantTopics] = useState<number>(0);
  const [collaborationStatements, setCollaborationStatements] = useState<SimilarStatement[]>([]);
  const [engagementStatements, setEngagementStatements] = useState<SimilarStatement[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState<number>(180);

  // Update chart size on container resize
  useEffect(() => {
    const updateChartSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = window.innerHeight * 0.5; // 50vh
        const newSize = Math.min(containerWidth - 16, containerHeight);
        setChartSize(Math.max(180, newSize)); // Minimum 180px, but can be larger
      }
    };

    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, []);

  useEffect(() => {
    calculateMatchAndData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statements]);

  const normalizeValue = (value: number, reverse: boolean): number => {
    // Normalize values to a scale from -1 to 1
    // For reverse categories, we flip the value
    return reverse ? -value / 2 : value / 2;
  };

  const calculateMatchAndData = () => {
    if (!statements || statements.length === 0) return;

    const targetUserData: UserAlignmentData = {};
    const currentUserData: UserAlignmentData = {};
    
    // Collect data for both users
    statements.forEach(statement => {
      const targetData = getTargetUserAlignmentData(statement.id);
      const currentData = getCurrentUserAlignmentData(statement.id);
      
      if (targetData) {
        targetUserData[statement.id] = {
          response: targetData.response,
          importance: targetData.importance
        };
      }
      
      if (currentData) {
        currentUserData[statement.id] = {
          response: currentData.response,
          importance: currentData.importance
        };
      }
    });

    // For calculating weighted match percentage
    let totalAlignmentScore = 0;
    let maxPossibleAlignmentScore = 0;
    let totalImportanceScore = 0;
    let maxPossibleImportanceScore = 0;
    
    let agreementCount = 0;
    let disagreementCount = 0;
    let mutualImportantCount = 0;
    
    // Initialize category scores with all categories
    const categoryScores: Record<string, { 
      targetSum: number; 
      currentSum: number; 
      targetCount: number;
      currentCount: number;
      matchScore: number;
      maxPossible: number;
    }> = {};
    
    // Initialize all categories (except foundation)
    CATEGORIES.forEach(category => {
      categoryScores[category.key] = { 
        targetSum: 0, 
        currentSum: 0, 
        targetCount: 0,
        currentCount: 0,
        matchScore: 0,
        maxPossible: 0
      };
    });
    
    const similarStatements: SimilarStatement[] = [];
    const differentStatements: SimilarStatement[] = [];
    
    statements.forEach(statement => {
      // Skip foundation category or invalid categories
      if (statement.category === "foundation" || !CATEGORY_KEYS.has(statement.category)) return;

      const targetData = targetUserData[statement.id];
      const currentData = currentUserData[statement.id];
      
      // Update category data even if only one user has responded
      if (targetData?.response !== undefined) {
        categoryScores[statement.category].targetSum += normalizeValue(targetData.response, statement.reverse);
        categoryScores[statement.category].targetCount++;
      }
      
      if (currentData?.response !== undefined) {
        categoryScores[statement.category].currentSum += normalizeValue(currentData.response, statement.reverse);
        categoryScores[statement.category].currentCount++;
      }
      
      // Only process agreement stats where both users have responded
      if (targetData?.response !== undefined && currentData?.response !== undefined) {
        // Calculate agreement level (5 = perfect match, 1 = complete opposite)
        const agreementLevel = 5 - Math.abs(targetData.response - currentData.response);
        
        // Update category alignment score (without considering importance)
        categoryScores[statement.category].matchScore += agreementLevel;
        categoryScores[statement.category].maxPossible += 5;
        
        // For overall alignment score (without importance)
        totalAlignmentScore += agreementLevel;
        maxPossibleAlignmentScore += 5;
        
        // Calculate importance match score
        const targetImportance = targetData.importance || 0;
        const currentImportance = currentData.importance || 0;
        
        // Calculate importance alignment (how similar their importance levels are)
        const importanceAgreement = 5 - Math.abs(targetImportance - currentImportance);
        totalImportanceScore += importanceAgreement;
        maxPossibleImportanceScore += 5;
        
        // Count agreements and disagreements
        if (agreementLevel >= 3) {
          agreementCount++;
          
          // If both users find it important, add to collaboration list
          if (targetImportance > 0 && currentImportance > 0) {
            mutualImportantCount++;
            similarStatements.push({
              id: statement.id,
              statement: statement.statement,
              currentUserImportance: currentImportance,
              targetUserImportance: targetImportance,
              agreement: agreementLevel
            });
          }
        } else {
          disagreementCount++;
          // If current user finds it important, add to engagement list
          if (currentImportance > 0) {
            differentStatements.push({
              id: statement.id,
              statement: statement.statement,
              currentUserImportance: currentImportance,
              targetUserImportance: targetImportance,
              agreement: agreementLevel
            });
          }
        }
      }
    });
    
    // Calculate pure alignment percentage (without importance)
    let alignmentPercentage = 0;
    let importancePercentageMatch = 0;
    
    if (maxPossibleAlignmentScore > 0) {
      alignmentPercentage = (totalAlignmentScore / maxPossibleAlignmentScore) * 100;
    }
    
    if (maxPossibleImportanceScore > 0) {
      importancePercentageMatch = (totalImportanceScore / maxPossibleImportanceScore) * 100;
    }
    
    // Store the pure alignment percentage
    setPureAlignmentPercentage(Math.round(alignmentPercentage));
    
    // Calculate final similaritea score with proper weighting
    const similaritea = (alignmentPercentage * ALIGNMENT_WEIGHT) + (importancePercentageMatch * IMPORTANCE_WEIGHT);
    
    setSimilariteaPercentage(Math.round(similaritea));
    
    // Update agreement/disagreement counts
    setSimilarTopics(agreementCount);
    setDifferentTopics(disagreementCount);
    setMutuallyImportantTopics(mutualImportantCount);
    
    // Prepare radar data for all categories
    const radarPoints: RadarPoint[] = CATEGORIES.map(({ key }) => {
      const data = categoryScores[key];
      const targetValue = data.targetCount > 0 ? data.targetSum / data.targetCount : null;
      const currentValue = data.currentCount > 0 ? data.currentSum / data.currentCount : null;
      
      // Calculate category alignment percentage (based only on alignment, not importance)
      const categoryAlignment = data.maxPossible > 0 
        ? Math.round((data.matchScore / data.maxPossible) * 100) 
        : 0;
      
      return {
        category: key,
        targetUserValue: targetValue,
        currentUserValue: currentValue,
        label: key, // Will be formatted in the render method
        alignment: categoryAlignment
      };
    });
    
    setRadarData(radarPoints);
    
    // Sort collaboration statements by combined importance
    const sortedSimilarStatements = similarStatements
      .sort((a, b) => {
        const aCombined = a.currentUserImportance + a.targetUserImportance;
        const bCombined = b.currentUserImportance + b.targetUserImportance;
        return bCombined - aCombined;
      })
      .slice(0, 3);
    
    setCollaborationStatements(sortedSimilarStatements);
    
    // Sort engagement statements by current user importance
    const sortedDifferentStatements = differentStatements
      .sort((a, b) => b.currentUserImportance - a.currentUserImportance)
      .slice(0, 3);
    
    setEngagementStatements(sortedDifferentStatements);
  };

  const renderRadarChart = () => {
    const size = chartSize;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size * 0.35; // Reduce radius to leave more space for labels
    
    // Scale font size based on chart size (as specified)
    const baseFontSize = Math.max(6, size / 28);
    
    // Draw radar chart
    return (
      <div className="flex justify-center py-2">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Grid lines */}
          <circle cx={centerX} cy={centerY} r={radius * 0.25} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
          <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
          <circle cx={centerX} cy={centerY} r={radius * 0.75} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
          <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
          
          {/* Category axes - always draw 8 axes */}
          {CATEGORIES.map((_, i) => {
            const angle = (i * 2 * Math.PI) / CATEGORIES.length;
            const x = centerX + radius * Math.sin(angle);
            const y = centerY - radius * Math.cos(angle);
            
            return (
              <line 
                key={`axis-${i}`}
                x1={centerX}
                y1={centerY}
                x2={x}
                y2={y}
                stroke="#888"
                strokeWidth="0.5"
                strokeOpacity="0.5"
              />
            );
          })}
          
          {/* Target user polygon */}
          <polygon
            points={CATEGORIES.map((category, i) => {
              const angle = (i * 2 * Math.PI) / CATEGORIES.length;
              const point = radarData.find(p => p.category === category.key);
              const value = point?.targetUserValue ?? 0; // Default to 0 if no value
              const distance = ((value + 1) / 2) * radius; // 0 to radius
              const x = centerX + distance * Math.sin(angle);
              const y = centerY - distance * Math.cos(angle);
              return `${x},${y}`;
            }).join(' ')}
            fill={TARGET_USER_FILL}
            stroke={TARGET_USER_COLOR}
            strokeWidth="1.5"
          />
          
          {/* Current user polygon */}
          <polygon
            points={CATEGORIES.map((category, i) => {
              const angle = (i * 2 * Math.PI) / CATEGORIES.length;
              const point = radarData.find(p => p.category === category.key);
              const value = point?.currentUserValue ?? 0; // Default to 0 if no value
              const distance = ((value + 1) / 2) * radius; // 0 to radius
              const x = centerX + distance * Math.sin(angle);
              const y = centerY - distance * Math.cos(angle);
              return `${x},${y}`;
            }).join(' ')}
            fill={CURRENT_USER_FILL}
            stroke={CURRENT_USER_COLOR}
            strokeWidth="1.5"
          />
          
          {/* Category labels with positions */}
          {CATEGORIES.map((category, i) => {
            const angle = (i * 2 * Math.PI) / CATEGORIES.length;
            let labelDistance = radius * 1.2; // Default distance for labels
            let xOffset = 0;
            let yOffset = 0;
            
            // Move specific categories
            if (i === 6 || i === 2) { // Left and right categories
              labelDistance = radius * 0.9; // Closer to center
            } else if (i === 1) { // Lift nationalism_cosmopolitanism category up by 20%
              yOffset = -radius * 0.1;
              xOffset = -radius * 0.26;
            } else if (i === 4) { // Move capitalism_socialism category right by 10%
              xOffset = radius * 0.2;
              yOffset = -radius * 0.05;
            } else if (i === 7) { 
              labelDistance = radius * 1.3;
            }
            
            const x = centerX + labelDistance * Math.sin(angle) + xOffset;
            const y = centerY - labelDistance * Math.cos(angle) + yOffset;
            
            // Find data point for this category
            const point = radarData.find(p => p.category === category.key);
            
            // Determine coloring based on aggregated values
            const targetValue = point?.targetUserValue ?? null;
            const currentValue = point?.currentUserValue ?? null;
            const alignment = point?.alignment ?? 0;
            
            // Text anchor based on position around the circle
            const quadrant = Math.floor((angle + Math.PI / 8) / (Math.PI / 4)) % 8;
            let textAnchor = "middle";
            if (quadrant === 0 || quadrant === 7) textAnchor = "middle";
            else if (quadrant >= 1 && quadrant <= 3) textAnchor = "start";
            else if (quadrant >= 4 && quadrant <= 6) textAnchor = "end";
            
            // Capitalize first letters
            const label1 = category.label1.charAt(0).toUpperCase() + category.label1.slice(1);
            const label2 = category.label2.charAt(0).toUpperCase() + category.label2.slice(1);
            
            // Determine label colors based on values
            const onSameSide = (targetValue !== null && currentValue !== null) && 
                               ((targetValue < 0 && currentValue < 0) || 
                                (targetValue > 0 && currentValue > 0));
            
            // New logic for label coloring
            let label1Color = DEFAULT_TEXT_COLOR;
            let label2Color = DEFAULT_TEXT_COLOR;
            
            if (targetValue !== null && targetValue < 0) {
              label1Color = TARGET_USER_COLOR;
            }
            
            if (currentValue !== null && currentValue > 0) {
              label2Color = CURRENT_USER_COLOR;
            }
            
            // If both users are on the same side, highlight only the relevant label
            if (onSameSide) {
              if (targetValue !== null && targetValue < 0) {
                // Both on negative side - highlight first label
                label1Color = HIGH_MATCH_COLOR;
              } else if (targetValue !== null && targetValue > 0) {
                // Both on positive side - highlight second label
                label2Color = HIGH_MATCH_COLOR;
              }
            }
            
            // Alignment percentage color
            const alignmentColor = alignment > 70 ? HIGH_MATCH_COLOR : DEFAULT_TEXT_COLOR;
            
            return (
              <g key={`label-${i}`}>
                {/* First word of the label */}
                <text
                  x={x}
                  y={y - baseFontSize * 0.7}
                  fontSize={baseFontSize}
                  fontWeight="500"
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  fill={label1Color}
                >
                  {label1}
                </text>
                
                {/* Second word of the label */}
                <text
                  x={x}
                  y={y + baseFontSize * 0.7}
                  fontSize={baseFontSize}
                  fontWeight="500"
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  fill={label2Color}
                >
                  {label2}
                </text>
                
                {/* Alignment percentage */}
                <text
                  x={x}
                  y={y + baseFontSize * 2}
                  fontSize={baseFontSize * 0.8}
                  fontWeight="bold"
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  fill={alignmentColor}
                >
                  {alignment > 0 ? `${alignment}%` : ""}
                </text>
              </g>
            );
          })}
          
          {/* Center point */}
          <circle cx={centerX} cy={centerY} r={1.5} fill="#666" />
        </svg>
      </div>
    );
  };

  // Calculate importance percentage for the bar
  const importancePercentage = Math.round(mutuallyImportantTopics / Math.max(similarTopics + differentTopics, 1) * 100);

  return (
    <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg space-y-2 h-full" ref={containerRef}>
      <div className="flex items-center justify-between gap-2">
        {/* Similaritea text */}
        <div className="flex font-semibold items-center flex-row gap-1 text-sm text-left text-eoe-text dark:text-eoe-dark-text w-fit">       
         Similaritea <span className="bg-eoe-icon dark:bg-eoe-dark-icon-hover flex flex-row items-center px-1.5 py-0 rounded-full text-eoe-text-sub/85 dark:text-white">{similariteaPercentage}<Blend className="w-4 h-4 ml-0.5 text-green-600" /></span>
        </div>
      </div>
      
      {/* Summary statement */}
      <div className="text-xs text-left text-eoe-text dark:text-eoe-dark-text px-0">
        You both <span className="underline">agree on {similarTopics}</span> statements, but <span className="underline">differ on {differentTopics}</span>, and
        find <span className="underline">{mutuallyImportantTopics} mutually important</span>.
      </div>
        
      {/* Legend */}
      <div className="flex items-center gap-2 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-sm mr-1" style={{ backgroundColor: TARGET_USER_COLOR }}></div>
          <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">Them</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-sm mr-1" style={{ backgroundColor: CURRENT_USER_COLOR }}></div>
          <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">You</span>
        </div>
      </div>
      
      {/* Responsive layout container for radar and bars */}
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center md:items-start">
        {/* Radar chart - adjusted for responsive layout */}
        <div className="w-full md:w-7/12 max-h-[50svh] flex justify-center">
          {renderRadarChart()}
        </div>
        
        {/* Percentage bars - adjusted for responsive layout */}
        <div className="w-full md:w-5/12 flex flex-col gap-3 justify-center">
          {/* Alignment percentage progress bar */}
          <div className="relative">
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">Alignment</p>
            <Progress value={pureAlignmentPercentage} className="bg-eoe-blue/20">
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                {pureAlignmentPercentage}%
              </div>
            </Progress>
          </div>
          
          {/* Importance percentage progress bar */}
          <div className="relative">
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">Importance</p>
            <Progress value={importancePercentage} className="bg-eoe-blue/20">
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                {importancePercentage}%
              </div>
            </Progress>
          </div>
        </div>
      </div>
      
      {/* Connect section */}
      <DetailPanel 
        title="Connect"
        className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
        defaultOpen={true}
      >
        {collaborationStatements.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-eoe-text dark:text-eoe-dark-text mb-1">
              Collaborate with {targetUsername} on these {collaborationStatements.length} topic{collaborationStatements.length !== 1 ? 's' : ''} you are both passionate about:
            </p>
            <div className="space-y-1">
              {collaborationStatements.map((item) => (
                <button
                  key={`collab-${item.id}`}
                  onClick={() => onStatementSelect(item.id)}
                  className="w-full px-2 py-1 text-left text-xs border rounded-md border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                >
                  <span className="border rounded-full px-1 mr-1 border-eoe-text dark:border-eoe-dark-text">{item.id}</span> {item.statement}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {engagementStatements.length > 0 && (
          <div>
            <p className="text-xs text-eoe-text dark:text-eoe-dark-text mb-1">
              Engage {targetUsername} on these {engagementStatements.length} topic{engagementStatements.length !== 1 ? 's' : ''} you don't agree on to build mutual understanding:
            </p>
            <div className="space-y-1">
              {engagementStatements.map((item) => (
                <button
                  key={`engage-${item.id}`}
                  onClick={() => onStatementSelect(item.id)}
                  className="w-full px-2 py-1 text-left text-xs border rounded-md border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                >
                  <span className="border rounded-full px-1 mr-1 border-eoe-text dark:border-eoe-dark-text">{item.id}</span> {item.statement}
                </button>
              ))}
            </div>
          </div>
        )}
      </DetailPanel>
    </div>
  );
};

export default AlignmentSummaryTile; 